import { useState } from 'react';

interface WelcomeWizardProps {
  onDismiss: () => void;
}

const features = [
  { icon: '⚡', title: 'Real-time Analytics', description: 'See who\'s on your site right now' },
  { icon: '🛒', title: 'E-commerce Tracking', description: 'Track purchases, add-to-cart, and checkout events' },
  { icon: '🔗', title: 'Traffic Sources', description: 'See where your visitors are coming from' },
  { icon: '🔥', title: 'Hourly Heatmap', description: 'Discover your peak traffic hours' },
  { icon: '📄', title: 'Pages Analysis', description: 'Understand your top landing and exit pages' },
  { icon: '💎', title: 'Flexible Plans', description: 'Free plan included, upgrade anytime for more' },
];

const setupSteps = [
  { number: 1, title: 'Authorize Your Site', description: 'Click "Authorize" on your site card to connect with Webflow' },
  { number: 2, title: 'Connect Google Account', description: 'Link your Google account to access GA4 data' },
  { number: 3, title: 'Select GA4 Property', description: 'Choose your GA4 account, property, and data stream' },
  { number: 4, title: 'Apply Tracking Code', description: 'Inject the GA4 tracking script into your Webflow site' },
  { number: 5, title: 'View Reports', description: 'You\'re all set! Open your analytics dashboard' },
];

const actionItems = [
  { label: 'View your reports', description: 'Check your analytics dashboard', path: '/dashboard' },
  { label: 'Explore new features', description: 'See what each plan offers', path: '/pricing' },
  { label: 'Visit Help Center', description: 'Get answers to common questions', path: '/help' },
];

export function WelcomeWizard({ onDismiss }: WelcomeWizardProps) {
  const [step, setStep] = useState(0);
  const totalSteps = 3;

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      onDismiss();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.card}>
        {/* Close button */}
        <button onClick={onDismiss} style={styles.closeButton} aria-label="Close">
          ✕
        </button>

        {/* Step content */}
        <div style={styles.content}>
          {step === 0 && <StepWhatNew />}
          {step === 1 && <StepSetup />}
          {step === 2 && <StepGetStarted onDismiss={onDismiss} />}
        </div>

        {/* Footer: dots + navigation */}
        <div style={styles.footer}>
          <div style={styles.dots}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                style={{
                  ...styles.dot,
                  backgroundColor: i === step ? '#2c6ecb' : '#d9dee3',
                }}
              />
            ))}
          </div>
          <div style={styles.navButtons}>
            {step > 0 && (
              <button onClick={handleBack} style={styles.backButton}>
                Back
              </button>
            )}
            <button onClick={handleNext} style={styles.nextButton}>
              {step === totalSteps - 1 ? 'Go to Dashboard' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepWhatNew() {
  return (
    <>
      <div style={styles.stepHeader}>
        <h2 style={styles.heading}>Welcome to Analyzely 2.0!</h2>
        <p style={styles.subtext}>
          We've added powerful new features to help you get the most from your analytics.
        </p>
      </div>
      <div style={styles.featureList}>
        {features.map((f) => (
          <div key={f.title} style={styles.featureItem}>
            <span style={styles.featureIcon}>{f.icon}</span>
            <div>
              <div style={styles.featureTitle}>{f.title}</div>
              <div style={styles.featureDesc}>{f.description}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function StepSetup() {
  return (
    <>
      <div style={styles.stepHeader}>
        <h2 style={styles.heading}>Setting Up Your Reports</h2>
        <p style={styles.subtext}>
          Follow these simple steps to start viewing your GA4 analytics.
        </p>
      </div>
      <div style={styles.setupList}>
        {setupSteps.map((s, i) => (
          <div key={s.number} style={styles.setupItem}>
            <div style={styles.setupLeft}>
              <div style={styles.setupNumber}>{s.number}</div>
              {i < setupSteps.length - 1 && <div style={styles.setupLine} />}
            </div>
            <div style={styles.setupContent}>
              <div style={styles.setupTitle}>{s.title}</div>
              <div style={styles.setupDesc}>{s.description}</div>
            </div>
          </div>
        ))}
      </div>
      <p style={styles.footerNote}>
        You'll find these steps on your Dashboard — just click on any site to get started.
      </p>
    </>
  );
}

function StepGetStarted({ onDismiss }: { onDismiss: () => void }) {
  const handleAction = (path: string) => {
    onDismiss();
    window.location.href = path;
  };

  return (
    <>
      <div style={styles.stepHeader}>
        <h2 style={styles.heading}>You're All Set!</h2>
        <p style={styles.subtext}>
          Here are a few things you can do right away.
        </p>
      </div>
      <div style={styles.actionList}>
        {actionItems.map((a) => (
          <div key={a.path} onClick={() => handleAction(a.path)} style={styles.actionItem}>
            <div style={styles.actionLabel}>{a.label}</div>
            <div style={styles.actionDesc}>{a.description}</div>
            <span style={styles.actionArrow}>→</span>
          </div>
        ))}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '580px',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#6d7175',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
    zIndex: 1,
  },
  content: {
    padding: '36px 32px 0',
  },
  stepHeader: {
    textAlign: 'center' as const,
    marginBottom: '24px',
  },
  heading: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#202223',
    margin: '0 0 8px',
  },
  subtext: {
    fontSize: '14px',
    color: '#6d7175',
    margin: 0,
    lineHeight: '1.5',
  },
  // Step 1: Features
  featureList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '10px 14px',
    borderRadius: '10px',
    backgroundColor: '#f9fafb',
  },
  featureIcon: {
    fontSize: '22px',
    flexShrink: 0,
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#202223',
  },
  featureDesc: {
    fontSize: '13px',
    color: '#6d7175',
    marginTop: '2px',
  },
  // Step 2: Setup
  setupList: {
    display: 'flex',
    flexDirection: 'column' as const,
    paddingLeft: '8px',
  },
  setupItem: {
    display: 'flex',
    gap: '16px',
    minHeight: '60px',
  },
  setupLeft: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    flexShrink: 0,
  },
  setupNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#2c6ecb',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    flexShrink: 0,
  },
  setupLine: {
    width: '2px',
    flexGrow: 1,
    backgroundColor: '#d9dee3',
    marginTop: '4px',
    marginBottom: '4px',
  },
  setupContent: {
    paddingTop: '4px',
    paddingBottom: '12px',
  },
  setupTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#202223',
  },
  setupDesc: {
    fontSize: '13px',
    color: '#6d7175',
    marginTop: '2px',
  },
  footerNote: {
    textAlign: 'center' as const,
    fontSize: '13px',
    color: '#8c9196',
    marginTop: '16px',
    fontStyle: 'italic',
  },
  // Step 3: Actions
  actionList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '10px',
    backgroundColor: '#f9fafb',
    textDecoration: 'none',
    cursor: 'pointer',
    border: '1px solid #e1e3e5',
    transition: 'background-color 0.15s',
  },
  actionLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#202223',
  },
  actionDesc: {
    fontSize: '13px',
    color: '#6d7175',
    flex: 1,
  },
  actionArrow: {
    fontSize: '18px',
    color: '#8c9196',
    marginLeft: 'auto',
  },
  // Footer
  footer: {
    padding: '20px 32px 28px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '16px',
  },
  dots: {
    display: 'flex',
    gap: '8px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    transition: 'background-color 0.2s',
  },
  navButtons: {
    display: 'flex',
    gap: '12px',
    width: '100%',
    justifyContent: 'center',
  },
  backButton: {
    padding: '10px 24px',
    borderRadius: '8px',
    border: '1px solid #c9cccf',
    backgroundColor: 'white',
    color: '#202223',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  nextButton: {
    padding: '10px 28px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2c6ecb',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
